import type { QuerySuggestionResult } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import { getCachedQuerySuggestionsOrFetch } from "@mirage/service-query-suggestions";
import * as primitives from "@mirage/service-typeahead-search/service/primitives";
import {
  SourceId,
  typeahead,
} from "@mirage/service-typeahead-search/service/types";
import { getSourceLimit } from "@mirage/service-typeahead-search/service/utils/source-helpers";
import * as wrappers from "@mirage/service-typeahead-search/service/utils/wrappers";
import * as rx from "rxjs";
import * as sourceScore from "../scoring/source-score";
import { twoStageSearch } from "../search/two-stage-search";
import { getResultSection } from "../utils/section-mapper";

const FILTER_RESULT_LIMIT = 50;

export const raw: typeahead.TaggedSearch = (
  query,
  mode,
  { config, grossLimit, sectionLimit },
) => {
  return rx.defer(() => {
    const sourceLimit = getSourceLimit(
      typeahead.ResultType.AskSuggestion,
      query,
      mode,
      config,
      grossLimit,
      sectionLimit,
    );

    const sectionId = getResultSection(
      typeahead.ResultType.AskSuggestion,
      mode,
      query,
      config,
    );

    return rx
      .from(
        getCachedQuerySuggestionsOrFetch({
          source: "dash",
          shouldHydrateCache: true,
        }),
      )
      .pipe(
        rx.map((allRetrievedQuerySuggestions) => {
          const dynamicSuggestions = allRetrievedQuerySuggestions.filter(
            (suggestion) => !suggestion.isStaticSuggestion,
          );

          if (!query.length && !!dynamicSuggestions.length) {
            return dynamicSuggestions
              .sort((a, b) => b.score! - a.score!)
              .slice(0, FILTER_RESULT_LIMIT)
              .map((result) => {
                const source = primitives.askSuggestion(result.uuid, result, {
                  sectionId,
                });

                return sourceScore.score(query, config)(source);
              })
              .sort((a, b) => b.score! - a.score!)
              .slice(0, sourceLimit);
          }

          const dynamicSuggestionsOrDemoSuggestions =
            dynamicSuggestions.length > 0
              ? dynamicSuggestions
              : (config.demoSuggestions ?? []);

          const sources = twoStageSearch<QuerySuggestionResult>(
            query,
            dynamicSuggestionsOrDemoSuggestions.map((querySuggestion) => ({
              searchableStrings: [querySuggestion.text],
              originalResult: querySuggestion,
            })),
            { limit: FILTER_RESULT_LIMIT },
          ).map((searchableResult) => searchableResult.originalResult);

          return sources
            .map((result) => {
              const source = primitives.askSuggestion(result.uuid, result, {
                sectionId,
              });

              return sourceScore.score(query, config)(source);
            })
            .sort((a, b) => b.score! - a.score!)
            .slice(0, sourceLimit);
        }),
      );
  });
};

export const search = wrappers.wrapped(SourceId.AskSuggestions, raw);
